/** US equity-market full-day closures. Keyed by YYYY-MM-DD in ET.
 *  Covers 2026–2028. Extend this list annually. Half-day early closes
 *  (day-after-Thanksgiving, Christmas Eve, etc.) are modeled separately
 *  in MARKET_EARLY_CLOSES below.
 *
 *  Source: NYSE/NASDAQ official holiday calendars.
 *  MUST stay byte-identical with api/src/utils/marketHolidays.ts
 *  (drift guard: web/src/__tests__/holidaySync.test.ts).
 */
export const MARKET_HOLIDAYS: Record<string, string> = {
  // 2026
  '2026-01-01': "New Year's Day",
  '2026-01-19': 'Martin Luther King, Jr. Day',
  '2026-02-16': "Presidents' Day",
  '2026-04-03': 'Good Friday',
  '2026-05-25': 'Memorial Day',
  '2026-06-19': 'Juneteenth',
  '2026-07-03': 'Independence Day (observed)',
  '2026-09-07': 'Labor Day',
  '2026-11-26': 'Thanksgiving Day',
  '2026-12-25': 'Christmas Day',

  // 2027
  '2027-01-01': "New Year's Day",
  '2027-01-18': 'Martin Luther King, Jr. Day',
  '2027-02-15': "Presidents' Day",
  '2027-03-26': 'Good Friday',
  '2027-05-31': 'Memorial Day',
  '2027-06-18': 'Juneteenth (observed)',
  '2027-07-05': 'Independence Day (observed)',
  '2027-09-06': 'Labor Day',
  '2027-11-25': 'Thanksgiving Day',
  '2027-12-24': 'Christmas Day (observed)',

  '2028-01-17': 'Martin Luther King, Jr. Day',
  '2028-02-21': "Presidents' Day",
  '2028-04-14': 'Good Friday',
  '2028-05-29': 'Memorial Day',
  '2028-06-19': 'Juneteenth',
  '2028-07-04': 'Independence Day',
  '2028-09-04': 'Labor Day',
  '2028-11-23': 'Thanksgiving Day',
  '2028-12-25': 'Christmas Day',
};

/**
 * Half-day early closes — NYSE/NASDAQ close at 1:00 PM ET (= 780 minutes
 * from midnight). Keyed by YYYY-MM-DD in ET, value is the close minute-of-day.
 * Distinct from full-day closures above: the regular session still opens at
 * 9:30 but ends early, so 1:00–4:00 PM ET is NOT regular trading.
 *
 * MUST stay byte-identical with api/src/utils/marketHolidays.ts (drift guard:
 * web/src/__tests__/holidaySync.test.ts). Vetted against the NYSE early-close
 * calendar — only dates where the early close actually applies are listed:
 *   - Day after Thanksgiving (Black Friday): every year.
 *   - Christmas Eve (Dec 24): only when it is a weekday AND not itself the
 *     observed Christmas closure (e.g. 2027-12-24 is the observed full
 *     holiday, so it is NOT a half day).
 *   - Day before Independence Day: only when July 4 is a weekday (so the
 *     preceding day is a normal trading day that closes early). When July 4
 *     falls on a weekend the observed full holiday absorbs it and there is
 *     no half day (e.g. 2026 + 2027 — both July 4 land on a weekend).
 * Extend this annually alongside MARKET_HOLIDAYS.
 */
export const MARKET_EARLY_CLOSES: Record<string, number> = {
  '2026-11-27': 780, // Day after Thanksgiving
  '2026-12-24': 780, // Christmas Eve (Thursday)
  '2027-11-26': 780, // Day after Thanksgiving
  '2028-07-03': 780, // Day before Independence Day (July 4 is a Tuesday)
  '2028-11-24': 780, // Day after Thanksgiving
};

/** Returns the holiday name if the given ET date (YYYY-MM-DD) is a
 *  full market closure, otherwise null. */
export function getHolidayName(etDate: string): string | null {
  return MARKET_HOLIDAYS[etDate] ?? null;
}

/** Returns the early-close minute-of-day (e.g. 780 = 1:00 PM ET) if `etDate`
 *  (YYYY-MM-DD) is a half-day, otherwise null. */
export function getEarlyCloseMinute(etDate: string): number | null {
  return MARKET_EARLY_CLOSES[etDate] ?? null;
}
